import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { api } from '../../lib/api';

/**
 * Homepage · Explore by Lifestyle
 *
 * A 12-tile magazine grid — visitors browse by "how they want to live" rather
 * than by geography. Every tile opens a dedicated SEO landing page under
 * /lifestyle/<slug> with photography, video, market insights and matched
 * neighbourhood recommendations.
 */
export default function LifestyleGrid() {
    const { data } = useQuery({
        queryKey: ['lifestyle-index'],
        queryFn: async () => (await api.get('/lifestyle')).data,
    });
    const items = data?.items || [];

    return (
        <section className="border-b border-neutral-200 bg-white" data-testid="lifestyle-grid">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-20 lg:pb-28">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
                    className="flex items-end justify-between gap-8 flex-wrap"
                >
                    <div className="max-w-3xl">
                        <div className="flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data">
                            <span className="inline-block w-6 h-px bg-neutral-400" />
                            Section 03 · Explore by Lifestyle
                        </div>
                        <h2
                            className="font-editorial tracking-editorial mt-6 leading-[1.03]"
                            style={{ fontSize: 'clamp(36px, 4.8vw, 68px)', letterSpacing: '-0.02em' }}
                        >
                            Browse by life,
                            <br />
                            <span className="italic text-neutral-500">not by map.</span>
                        </h2>
                        <p className="mt-6 font-editorial text-neutral-600 max-w-2xl" style={{ fontSize: 'clamp(16px, 1.15vw, 19px)', lineHeight: 1.55 }}>
                            Twelve editorial routes into Southern Ontario — each one a landing page with the
                            photography, the video, the market insights and the neighbourhoods that match.
                        </p>
                    </div>
                </motion.div>

                {/* Grid — 4 across on desktop, 3 on tablet-large, 2 on tablet, 1 on mobile */}
                <div className="mt-14 lg:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-0 border-l border-t border-neutral-200">
                    {items.map((c, i) => (
                        <motion.div
                            key={c.slug}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-40px' }}
                            transition={{ duration: 0.55, delay: (i % 4) * 0.06, ease: [0.22, 0.61, 0.36, 1] }}
                            className="border-r border-b border-neutral-200"
                        >
                            <Link
                                to={`/lifestyle/${c.slug}`}
                                data-testid={`lifestyle-card-${c.slug}`}
                                className="group block relative overflow-hidden bg-neutral-200"
                                style={{ aspectRatio: '4 / 5' }}
                            >
                                {/* Image */}
                                <img
                                    src={c.hero_image}
                                    alt={c.name}
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                                />
                                {/* Gradient scrim for readability */}
                                <div
                                    className="absolute inset-0 pointer-events-none"
                                    style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.75) 100%)' }}
                                />
                                {/* Number top-left */}
                                <div className="absolute top-4 left-4 font-mono-data text-[11px] tracking-[0.24em] uppercase text-white/70 tabular">
                                    {String(i + 1).padStart(2, '0')}
                                </div>
                                {/* Arrow top-right (reveals on hover) */}
                                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                    <ArrowUpRight className="w-4 h-4 text-white" />
                                </div>
                                {/* Bottom text */}
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <div
                                        className="font-editorial tracking-editorial text-white leading-tight"
                                        style={{ fontSize: '26px', letterSpacing: '-0.01em' }}
                                    >
                                        {c.name}
                                    </div>
                                    <div className="mt-2 text-[12.5px] text-white/80 leading-snug italic font-editorial">
                                        {c.tagline}
                                    </div>
                                </div>
                                {/* Hairline accent — animates in on hover */}
                                <div className="absolute bottom-0 left-0 h-px bg-white w-0 group-hover:w-full transition-[width] duration-700 ease-out" />
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
